'use client';

import { LocalStorageKeys } from '@/lib/local-storage';
import { createZustandShallowStore } from '@/lib/zustand';
import { check_version_cookies } from '@/server/common/utils/cookie.action';
import { useEffect, useState } from 'react';
import { persist } from 'zustand/middleware';

const CURRENT_APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION || '';

interface AppVersionStoreProps {
    /**
     * Version của app
     * Chỉ dùng để check version
     */
    currentVersion: string | null;

    setCurrentVersion: (version: string) => void;
}

const useAppVersionStore = createZustandShallowStore<AppVersionStoreProps>()(
    persist(
        (set, get) => {
            return {
                isLoading: true,

                currentVersion: null,
                setCurrentVersion(version: string) {
                    set((st) => {
                        return {
                            currentVersion: version,
                        };
                    });
                },
            };
        },
        {
            name: LocalStorageKeys.App_Version,
            onRehydrateStorage() {
                return (st, error) => {
                    if (error) {
                        console.error(`Has error after Rehydrate Storage`);
                    } else if (st) {
                        const version = st.currentVersion;
                        if (version) {
                            st.setCurrentVersion(version);
                        } else {
                            st.setCurrentVersion(CURRENT_APP_VERSION);
                        }
                    }
                };
            },
        }
    )
);

/**
 * Cờ sessionStorage đánh dấu "đã reload một lần cho version này trong phiên tab này".
 * Giá trị = version đích, để deploy version SAU vẫn được reload lại đúng một lần.
 */
const RELOAD_GUARD_KEY = 'app-version-reloaded';

/**
 * Không chạy version-check trên trang đăng nhập SSO: ở `/signin?sso=auto` component này
 * mount CÙNG LÚC với SsoAutoRedirect (đẩy sang Keycloak). Nếu reload chen vào sẽ cắt ngang
 * chuỗi redirect đăng nhập → trên thiết bị chậm/mobile gây lỗi hoặc loop. Bao luôn
 * `/signin/sso` (dù route handler không render React, phòng trường hợp đổi cấu trúc).
 */
function isSigninPath(pathname: string): boolean {
    return /\/signin(\/|$)/.test(pathname);
}

export const useCheckAppVersion = () => {
    const { currentVersion, setCurrentVersion } = useAppVersionStore();

    const [isCheckingVersion, setIsCheckingVersion] = useState(true);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        // Chưa rehydrate xong (null) thì chờ lần effect sau khi store có giá trị.
        if (currentVersion === null) return;

        if (currentVersion === CURRENT_APP_VERSION) {
            setIsCheckingVersion(false);
            return;
        }

        // Đang trong luồng đăng nhập SSO — tuyệt đối không reload để khỏi cắt ngang redirect.
        if (isSigninPath(window.location.pathname)) {
            setIsCheckingVersion(false);
            return;
        }

        check_version_cookies(currentVersion)
            .then((serverOnNewVersion) => {
                // Server chưa thực sự lên version mới (rollout nhiều instance) → không reload.
                if (!serverOnNewVersion) return;

                // Loop-breaker: chỉ reload TỐI ĐA MỘT LẦN/phiên tab cho mỗi version, và chỉ
                // khi ghi được cờ vào sessionStorage. Trên thiết bị storage bị chặn (chế độ
                // riêng tư, ITP...) localStorage có thể KHÔNG lưu được version mới → nếu cứ
                // reload sẽ lặp vô hạn. Khi không track được, ta bỏ qua reload và chạy tiếp
                // với asset hiện có thay vì kẹt vòng lặp.
                let canTrack = false;
                try {
                    if (
                        sessionStorage.getItem(RELOAD_GUARD_KEY) ===
                        CURRENT_APP_VERSION
                    ) {
                        // Đã reload một lần cho version này rồi mà vẫn lệch (ghi localStorage
                        // không ăn) → dừng, không reload nữa.
                        setCurrentVersion(CURRENT_APP_VERSION);
                        return;
                    }
                    sessionStorage.setItem(
                        RELOAD_GUARD_KEY,
                        CURRENT_APP_VERSION
                    );
                    canTrack =
                        sessionStorage.getItem(RELOAD_GUARD_KEY) ===
                        CURRENT_APP_VERSION;
                } catch {
                    canTrack = false;
                }

                setCurrentVersion(CURRENT_APP_VERSION);

                if (canTrack) {
                    window.location.reload();
                }
            })
            .finally(() => {
                setIsCheckingVersion(false);
            });
    }, [currentVersion, setCurrentVersion]);

    return isCheckingVersion;
};
